/**
 * AccountFormDialog — thin router that dispatches to the correct
 * category-specific form:
 *
 *   Cash        → CashAccountForm       (writes `accounts`)
 *   Investments → InvestmentForm        (writes `wealth_holdings`)
 *   Liabilities → LiabilityForm         (writes `liabilities`)
 *
 * Special-case: credit-card liabilities live in the `accounts` table
 * (type='credit_card'). When editing a credit card from the Liabilities
 * tab we still show the CashAccountForm so all the same fields (last4,
 * currency, credit-card copy) surface. This preserves the legacy schema
 * split without exposing it to the UI.
 */
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import CashAccountForm from './CashAccountForm';
import InvestmentForm  from './InvestmentForm';
import LiabilityForm   from './LiabilityForm';

const TITLES = {
  cash:        { add: 'New account',        edit: 'Edit account' },
  investments: { add: 'Add a holding',      edit: 'Edit holding' },
  liabilities: { add: 'Add a liability',    edit: 'Edit liability' },
};

export default function AccountFormDialog({
  open,
  onOpenChange,
  category,        // 'cash' | 'investments' | 'liabilities'
  account = null,  // NormalisedAccount when editing; null when creating
  onCreate,
  onUpdate,
  baseCurrency = 'INR',
  presetType = null,   // Phase 86.K — open on a specific account type
}) {
  const editing = !!account;

  // Route to the correct sub-form. If editing an existing row we let
  // its `source_table` decide (a credit-card liability lives in the
  // `accounts` table so we render CashAccountForm for it).
  let FormComponent;
  let effectiveCategory = category;

  if (editing) {
    if (account.source_table === 'accounts')             { FormComponent = CashAccountForm;   effectiveCategory = 'cash'; }
    else if (account.source_table === 'wealth_holdings') { FormComponent = InvestmentForm;    effectiveCategory = 'investments'; }
    else                                                  { FormComponent = LiabilityForm;    effectiveCategory = 'liabilities'; }
  } else {
    if (category === 'cash')             FormComponent = CashAccountForm;
    else if (category === 'investments') FormComponent = InvestmentForm;
    else                                 FormComponent = LiabilityForm;
  }

  const title = TITLES[effectiveCategory]?.[editing ? 'edit' : 'add'] || (editing ? 'Edit' : 'Add');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-md rounded-2xl max-h-[92vh] overflow-y-auto"
        data-testid="accounts-form-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display">{title}</DialogTitle>
        </DialogHeader>
        {open && (
          <FormComponent
            account={account}
            onCreate={onCreate}
            onUpdate={onUpdate}
            onClose={() => onOpenChange(false)}
            baseCurrency={baseCurrency}
            presetType={presetType}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
